import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { GraphQLClientService } from '../../../../../core/services/graphql-client.service';
import {
  BookMeetingService,
  MeetingAttendee,
  AttendeeMode,
} from '../../../../../core/services/book-meeting.service';
import { LocaleService } from '../../../../../core/services/locale.service';
import { ViewerIdentityService } from '../../../../../core/services/viewer-identity.service';
import { PaperIconComponent, PaperButtonComponent } from '@ricoh-paper/angular';
import { getInitials } from '../../../../../core/utils/text';
import {
  BmpAttendeeChipComponent,
  type AttendeePresence,
  type AttendeeRole,
} from './bmp-attendee-chip.component';

interface SearchResult {
  id: string;
  name: string;
  email: string;
  department: string | null;
  picture: string | null;
}

const EMPLOYEES_QUERY = `
  query employees($search: String!, $take: Int) {
    employees(search: $search, take: $take) {
      _id email name firstName lastName department picture
    }
  }
`;

// Hue-seed for avatar background (matches prototype)
function stringHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}

@Component({
  selector: 'im-attendees-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperIconComponent, PaperButtonComponent, BmpAttendeeChipComponent, TranslocoPipe],
  template: `
    <div class="as">
      <!-- Attendee list -->
      <div class="as__list">
        @for (att of meetingService.attendees(); track att.email) {
          <div class="as__row" [class.as__row--readonly]="readonly()">
            <!-- Avatar -->
            <span class="as__avatar" [style.background]="att.picture ? 'url(' + att.picture + ') center/cover' : avatarBg(att.name)" [style.color]="avatarFg(att.name)">
              @if (!att.picture) { {{ initials(att.name) }} }
            </span>

            <!-- Name + pills -->
            <div class="as__info">
              <div class="as__name-row">
                <span class="as__name">{{ att.name }}</span>
                @if (readonly()) {
                  @if (att.isOrganiser) {
                    <span class="as__role-chip as__role-chip--organizer">{{ 'viewer.book_meeting_panel.attendees.organizer' | transloco }}</span>
                  }
                  @if (isViewer(att)) {
                    <span class="as__role-chip as__role-chip--you">{{ 'viewer.book_meeting_panel.attendees.you' | transloco }}</span>
                  }
                  @if (att.external) {
                    <span class="as__role-chip as__role-chip--ext">{{ 'viewer.book_meeting_panel.attendees.external' | transloco }}</span>
                  }
                } @else {
                  @if (att.isHost) {
                    <span class="as__role-chip as__role-chip--host">{{ 'viewer.book_meeting_panel.attendees.host_upper' | transloco }}</span>
                  }
                  @if (att.isOrganiser && !att.isHost) {
                    <span class="as__role-chip as__role-chip--org">{{ 'viewer.book_meeting_panel.attendees.org_upper' | transloco }}</span>
                  }
                  @if (att.external) {
                    <span class="as__role-chip as__role-chip--ext">{{ 'viewer.book_meeting_panel.attendees.external_upper' | transloco }}</span>
                  }
                }
              </div>
              <div class="as__meta">
                @if (readonly()) {
                  <span>{{ att.jobTitle || att.department || att.office || att.email }}</span>
                } @else {
                  <span>{{ att.office || att.email }}</span>
                }
                <!-- Free / Busy / Tentative / OOF status dot. Pulls
                     from BookMeetingService.conflictsByAttendee(); the
                     organiser/self doesn't get one (they're booking).
                     Hidden until the panel has a time window selected
                     (no startTime → conflict map is empty anyway). -->
                @if (!att.isOrganiser && meetingService.startTime()) {
                  @let availability = attendeeAvailability(att);
                  <span class="as__avail" [attr.data-status]="availability.key"
                        [attr.title]="availability.title">
                    <span class="as__avail-dot" aria-hidden="true"></span>
                    {{ availability.label }}
                  </span>
                }
                @if (att.location) {
                  <span class="as__location-pill" [attr.data-location]="att.location">
                    <span class="as__location-dot"></span>
                    {{ locationLabel(att.location) }}
                  </span>
                }
              </div>
            </div>

            @if (!readonly()) {
              <!-- Schedule V2 — single combined role + presence chip,
                   replacing the original segmented role toggle + mode
                   toggle. One pill, one popover, two sections inside.
                   Every chip is interactive (including the viewer's own
                   row) so the viewer can promote another attendee to
                   host and demote themselves. Single-host + zero-host
                   invariants enforced server-side AND in
                   BookMeetingService.setAttendeeRole. -->
              <div class="as__controls">
                <bmp-attendee-chip
                  [role]="attRole(att)"
                  [presence]="attPresence(att)"
                  [locked]="false"
                  (roleChange)="onChipRoleChange(att.id, $event)"
                  (presenceChange)="onChipPresenceChange(att.id, $event)" />
                <paper-button
                  class="as__remove"
                  intent="ghost" shape="icon" size="xs"
                  [class.as__remove--hidden]="att.isHost"
                  icon="x" [iconSize]="11"
                  [disabled]="att.isHost"
                  [ariaLabel]="'viewer.book_meeting_panel.attendees.remove_attendee' | transloco"
                  (click)="meetingService.removeAttendee(att.email)" />
              </div>
            } @else {
              <!-- Read-only response pill — keyed off the Graph mirror's
                   responseStatus. Falls back to the attendee mode (Remote /
                   On-site) when no RSVP state is known (e.g., create-flow
                   attendees added by the user but not yet sync'd). -->
              @if (responsePill(att); as pill) {
                <span class="as__status-pill" [attr.data-tone]="pill.tone">{{ pill.label }}</span>
              } @else if (att.mode === 'remote') {
                <span class="as__readonly-mode">{{ 'viewer.book_meeting_panel.attendees.remote' | transloco }}</span>
              } @else {
                <span class="as__readonly-mode">{{ 'viewer.book_meeting_panel.attendees.on_site' | transloco }}</span>
              }
            }
          </div>
        }
      </div>

      @if (!readonly()) {
      <!-- Search input -->
      <div class="as__search-wrap">
        <paper-icon [name]="isEmailQuery() ? 'mail' : 'search'" [size]="13"
          class="as__search-icon" [class.email]="isEmailQuery()" />
        <input
          type="text"
          class="as__search"
          [placeholder]="'viewer.book_meeting_panel.attendees.search_placeholder' | transloco"
          [value]="searchQuery()"
          (input)="onInput($any($event.target).value)"
          (focus)="onFocus()"
          (blur)="onBlur()"
          (keydown.enter)="onEnter()"
          [attr.aria-label]="'viewer.book_meeting_panel.attendees.search_people' | transloco" />
      </div>

      <!-- Search results dropdown -->
      @if (showResults() && (filteredResults().length > 0 || isEmailQuery() || searchError())) {
        <div class="as__results">
          @if (searchError()) {
            <div class="as__result-error" role="alert">
              <paper-icon name="alert-triangle" [size]="12" />
              <span>{{ 'viewer.book_meeting_panel.attendees.search_error' | transloco }}</span>
              <paper-button class="as__result-retry" intent="secondary" size="xs" (mousedown)="retrySearch()">{{ 'viewer.book_meeting_panel.attendees.try_again' | transloco }}</paper-button>
            </div>
          }
          @for (person of filteredResults(); track person.email) {
            <button class="as__result" (mousedown)="addPerson(person)">
              <span class="as__result-avatar"
                [style.background]="person.picture ? 'url(' + person.picture + ') center/cover' : 'hsl(' + hue(person.name) + ' 32% 28%)'"
                [style.color]="'hsl(' + hue(person.name) + ' 70% 82%)'">
                @if (!person.picture) { {{ initials(person.name) }} }
              </span>
              <div class="as__result-info">
                <div class="as__result-name">{{ person.name }}</div>
                <div class="as__result-meta">
                  <span>{{ person.email }}</span>
                  @if (person.department) {
                    <span> · {{ person.department }}</span>
                  }
                </div>
              </div>
            </button>
          }
          @if (isEmailQuery() && !filteredResults().length) {
            <button class="as__result as__result--external" (mousedown)="addExternalByEmail()">
              <span class="as__result-avatar as__result-avatar--ext">
                <paper-icon name="user-plus" [size]="13" />
              </span>
              <div class="as__result-info">
                <div class="as__result-name as__icon-ext">{{ 'viewer.book_meeting_panel.attendees.invite' | transloco }} {{ searchQuery() }}</div>
                <div class="as__result-meta as__icon-ext-dim">{{ 'viewer.book_meeting_panel.attendees.external_visitor_pass' | transloco }}</div>
              </div>
              <span class="as__result-enter">↵</span>
            </button>
          }
          @if (searchQuery() && !isEmailQuery() && filteredResults().length === 0) {
            <div class="as__result-empty">{{ 'viewer.book_meeting_panel.attendees.no_one_matches' | transloco }}</div>
          }
        </div>
      }
      }

      <!-- External attendee hint -->
      @if (hasExternal()) {
        <div class="as__external-hint">
          <paper-icon name="badge-check" [size]="11" class="as__icon-warn" />
          <span>{{ 'viewer.book_meeting_panel.attendees.visitor_pass_queued' | transloco }}</span>
        </div>
      }

      @if (readonly() && meetingService.attendees().length === 0) {
        @if (meetingService.attendeesAreRedacted()) {
          <p class="as__readonly-empty">
            {{ redactedAttendeesLabel(meetingService.loadedAttendeeCount()) }}
            <span class="as__readonly-redacted">{{ 'viewer.book_meeting_panel.attendees.details_hidden' | transloco }}</span>
          </p>
        } @else {
          <p class="as__readonly-empty">{{ 'viewer.book_meeting_panel.attendees.no_attendees' | transloco }}</p>
        }
      }
    </div>
  `,
  styles: [`
    /* Reset browser button defaults on search result buttons */
    .as__result {
      background: none;
      border: none;
      color: inherit;
      cursor: pointer;
      font: inherit;
    }

    .as { display: flex; flex-direction: column; gap: 12px; position: relative; }

    /* ── Attendee rows ────────────────────── */
    .as__list { display: flex; flex-direction: column; gap: 6px; margin-bottom: 4px; }

    .as__row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 10px;
      border-radius: 9px;
      background: var(--hover-overlay);
      border: 1px solid var(--divider);
      flex-wrap: wrap;
    }

    .as__avatar {
      display: flex; align-items: center; justify-content: center;
      width: 28px; height: 28px; border-radius: 50%;
      font-size: 10px; font-weight: 600;
      border: 1px solid var(--border-low);
      flex: none;
    }

    .as__info { min-width: 0; flex: 1; }

    .as__name-row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
    .as__name { font-size: 12.5px; font-weight: 500; color: var(--text); }

    .as__role-chip {
      padding: 1px 5px; border-radius: 3px;
      font-size: 10px; font-weight: 700; letter-spacing: 0.08em; /* type-scale floor */
      display: inline-flex; align-items: center; gap: 4px;

      &--host { background: var(--bmp-badge-primary-bg); color: var(--bmp-badge-primary); }
      &--org { background: var(--bmp-badge-ok-bg); color: var(--bmp-badge-ok); }
      &--ext { background: var(--bmp-ext-bg); color: var(--bmp-ext-text); }
      &--conflict { background: var(--bmp-badge-danger-bg); color: var(--bmp-badge-danger); }
      &--tentative { background: var(--bmp-badge-warn-bg); color: var(--bmp-badge-warn); }
    }

    .as__conflict-dot {
      width: 5px; height: 5px; border-radius: 50%;
      background: currentColor;
    }

    // Themed icon colour classes (replacing inline hex)
    .as__icon-indigo { color: var(--bmp-indigo-text); }
    .as__icon-accent { color: var(--bmp-accent-text); }
    .as__icon-ext { color: var(--bmp-ext-text); }
    .as__icon-ext-dim { color: var(--bmp-ext-text); opacity: 0.6; }
    .as__icon-warn { color: var(--bmp-badge-warn); }

    .as__meta {
      font-size: 10.5px; color: var(--text-tertiary); margin-top: 1px;
      display: flex; align-items: center; gap: 6px;
    }

    /* Inline availability — dot + label in the meta row showing
       whether the attendee is Free / Busy / Tentative / OOF in the
       proposed meeting window. Drives the "are they available?"
       read without forcing the user to open the find-time strip. */
    .as__avail {
      display: inline-flex; align-items: center; gap: 4px;
      font-size: 10.5px; font-weight: 500;
    }
    .as__avail-dot {
      width: 6px; height: 6px; border-radius: 50%;
      flex: none;
    }
    .as__avail[data-status="free"] {
      color: var(--success-strong);
      .as__avail-dot { background: var(--success-strong); }
    }
    .as__avail[data-status="tentative"] {
      color: var(--warning-strong);
      .as__avail-dot { background: var(--warning-strong); }
    }
    .as__avail[data-status="busy"],
    .as__avail[data-status="oof"] {
      color: var(--bmp-badge-danger, var(--warning-strong));
      .as__avail-dot { background: var(--bmp-badge-danger, var(--warning-strong)); }
    }

    .as__location-pill {
      display: inline-flex; align-items: center; gap: 3px;
      padding: 0 4px; font-size: 10px; font-weight: 600; /* type-scale floor */
      letter-spacing: 0.04em; text-transform: uppercase;
    }

    .as__location-dot { width: 5px; height: 5px; border-radius: 50%; }

    [data-location="in-office"] { color: var(--bmp-loc-office); .as__location-dot { background: var(--bmp-loc-office); } }
    [data-location="remote"] { color: var(--bmp-loc-remote); .as__location-dot { background: var(--bmp-loc-remote); } }
    [data-location="on-leave"] { color: var(--bmp-loc-leave); .as__location-dot { background: var(--bmp-loc-leave); } }
    [data-location="external"] { color: var(--bmp-loc-external); .as__location-dot { background: var(--bmp-loc-external); } }

    /* ── Role toggle (segmented pill — matches mode toggle) ──── */
    .as__role-toggle {
      display: inline-flex; padding: 1px;
      background: var(--focus-overlay);
      border-radius: 100px; border: 1px solid var(--divider);
      flex: none;
    }

    .as__role-btn {
      padding: 3px 8px; font-size: 10px; border-radius: 100px;
      background: transparent; color: var(--text-tertiary);
      display: inline-flex; align-items: center; gap: 3px;
      font-weight: 600; cursor: pointer; font: inherit;
      font-size: 10px; font-weight: 600;
      border: none;
      transition: all 0.15s ease;

      &.active {
        background: var(--glass-border);
        color: var(--text);
      }

      &--org.active {
        background: var(--bmp-accent-soft, color-mix(in srgb, var(--success-strong) 12%, transparent));
        color: var(--bmp-accent-text, var(--success-strong));
      }

      &--host.active {
        background: var(--bmp-badge-primary-bg, color-mix(in srgb, var(--brand-primary) 18%, transparent));
        color: var(--bmp-badge-primary, var(--brand-primary));
      }
    }

    /* ── Mode toggle ─────────────────────── */
    .as__controls { display: flex; align-items: center; gap: 4px; }

    .as__mode-toggle {
      display: inline-flex; padding: 1px;
      background: var(--focus-overlay);
      border-radius: 100px; border: 1px solid var(--divider);
      flex: none;
    }

    .as__mode-btn {
      padding: 3px 8px; font-size: 10px; border-radius: 100px;
      background: transparent; color: var(--text-tertiary);
      display: inline-flex; align-items: center; gap: 3px;
      font-weight: 600; cursor: pointer; font: inherit;
      font-size: 10px; font-weight: 600;
      border: none;
      transition: all 0.15s ease;

      &.active {
        background: var(--bmp-accent-soft, color-mix(in srgb, var(--success-strong) 12%, transparent));
        color: var(--bmp-accent-text, var(--success-strong));
      }

      &--remote.active {
        background: color-mix(in srgb, oklch(0.62 0.18 295) 20%, transparent);
        color: var(--bmp-remote-text, oklch(0.62 0.18 295));
      }
    }

    .as__remove {
      width: 24px; height: 24px; border-radius: 6px;
      display: flex; align-items: center; justify-content: center;
      color: var(--text-tertiary); cursor: pointer; flex: none;
      background: none; border: none; font: inherit;
      transition: background 0.15s, color 0.15s;

      &:hover:not(:disabled) { background: var(--bmp-badge-danger-bg); color: var(--bmp-badge-danger); }

      &--hidden { visibility: hidden; pointer-events: none; }
    }

    /* ── Search ───────────────────────────── */
    .as__search-wrap { position: relative; }

    .as__search-icon {
      position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
      color: var(--text-tertiary);
      &.email { color: var(--bmp-badge-warn); }
    }

    .as__search {
      width: 100%; padding: 10px 12px 10px 34px; font-size: 12.5px;
      background: var(--focus-overlay);
      border: 1px solid var(--divider);
      border-radius: 8px; color: var(--text); outline: none;
      transition: border-color 0.15s;

      &::placeholder { color: var(--text-tertiary); }
      &:focus { border-color: color-mix(in srgb, var(--success-strong) 25%, transparent); background: var(--pressed-overlay); }
    }

    /* ── Results dropdown ─────────────────── */
    .as__results {
      position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 5;
      border-radius: 10px;
      background: var(--surface-1);
      border: 1px solid var(--border-med);
      padding: 4px;
      max-height: 280px; overflow-y: auto;
      box-shadow: var(--panel-shadow);
    }

    .as__result {
      display: flex; align-items: center; gap: 10px;
      padding: 7px; width: 100%; border-radius: 7px; text-align: left;
      cursor: pointer; color: var(--text);

      &:hover { background: var(--hover-overlay); }
    }

    .as__result--external {
      background: var(--bmp-ext-bg);
      border: 1px dashed var(--bmp-ext-bg);
      margin-top: 4px;

      &:hover { background: var(--bmp-ext-bg); filter: brightness(1.2); }
    }

    .as__result-avatar {
      width: 32px; height: 32px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 11px; font-weight: 600; flex: none;
      border: 1px solid var(--glass-border);
      background-size: cover;

      &--ext {
        background: var(--bmp-ext-bg); color: var(--bmp-ext-text);
      }
    }

    .as__result-info { flex: 1; min-width: 0; }
    .as__result-name { font-size: 12px; color: var(--text); }
    .as__result-meta { font-size: 10px; color: var(--text-tertiary); margin-top: 1px; }
    .as__result-dept { font-size: 10px; color: var(--text-tertiary); }
    .as__result-enter {
      font-size: 10px; color: var(--bmp-ext-text); padding: 2px 7px;
      background: var(--bmp-ext-bg); border-radius: 100px;
    }
    .as__result-empty { padding: 10px; font-size: 11px; color: var(--text-tertiary); text-align: center; }

    /* Search error row — replaces the otherwise-silent "no results"
       state when the GraphQL query fails. Warning-tinted so the user
       knows to retry rather than abandon the field. */
    .as__result-error {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 10px;
      font-size: 11.5px;
      color: var(--warning-strong);
      background: color-mix(in srgb, var(--warning-strong) 8%, transparent);
      border-radius: 6px;
    }
    .as__result-retry {
      margin-left: auto;
      font-size: 11px;
      font-weight: 500;
      font-family: inherit;
      color: var(--text);

      &:focus-visible {
        outline: 2px solid var(--brand-focus-ring, var(--color-primary));
        outline-offset: 1px;
      }
    }

    /* ── External hint ────────────────────── */
    .as__external-hint {
      margin-top: 4px; padding-left: 2px;
      display: flex; align-items: center; gap: 6px;
      font-size: 11px; color: var(--text-tertiary);
    }

    /* ── Read-only mode tag (per attendee row) ── */
    .as__readonly-mode {
      padding: 3px 8px;
      font-size: 10px; font-weight: 600;
      letter-spacing: 0.04em;
      color: var(--text-tertiary);
      background: var(--focus-overlay);
      border: 1px solid var(--divider);
      border-radius: 100px;
      flex: none;
    }

    .as__readonly-empty {
      margin: 0; padding: 8px 0;
      font-size: 11.5px; color: var(--text-tertiary);
      font-style: italic;
    }

    .as__readonly-redacted {
      margin-left: 4px;
      font-style: normal;
      opacity: 0.75;
    }

    /* ── View-mode row enrichment ──────────────────────────
       In readonly mode the row gets a slightly larger avatar (32px) and
       a tightened meta line so the jobTitle / department subtitle reads
       cleanly. The status pill on the right replaces the on-site/remote
       toggle that's hidden in view mode. */
    .as__row--readonly {
      align-items: center;
      gap: 12px;

      .as__avatar {
        width: 32px;
        height: 32px;
        font-size: 12px;
      }

      .as__meta {
        font-size: 11.5px;
      }
    }

    /* Status pill — colour-coded by RSVP state. Ton tokens:
         ok (green)     → Accepted
         warn (amber)   → Tentative
         danger (red)   → Declined
         neutral (slate)→ Pending
       Sized to match Space-panel pill conventions (10px caps). */
    .as__status-pill {
      padding: 3px 9px;
      font-size: 10px;
      font-weight: 600;
      letter-spacing: 0.04em;
      border-radius: 100px;
      flex: none;
      border: 1px solid transparent;
      text-transform: none;

      &[data-tone="ok"] {
        color: var(--success-text, var(--success-strong));
        background: var(--success-soft, color-mix(in srgb, var(--success-strong) 12%, transparent));
        border-color: color-mix(in srgb, var(--success-strong) 25%, transparent);
      }
      &[data-tone="warn"] {
        color: var(--warning-text, var(--warning-strong));
        background: var(--warning-soft, color-mix(in srgb, var(--warning-strong) 12%, transparent));
        border-color: color-mix(in srgb, var(--warning-strong) 25%, transparent);
      }
      &[data-tone="danger"] {
        color: var(--error-text, var(--error-strong));
        background: var(--error-soft, color-mix(in srgb, var(--error-strong) 12%, transparent));
        border-color: color-mix(in srgb, var(--error-strong) 25%, transparent);
      }
      &[data-tone="neutral"] {
        color: var(--text-tertiary);
        background: var(--hover-overlay);
        border-color: var(--divider);
      }
    }

    /* Inline name chips — Organizer (teal) + You (subtle neutral) match
       the old meeting-detail-panel's chip palette so the chrome stays
       coherent with the rest of the redesign. */
    .as__role-chip--organizer {
      color: oklch(0.69 0.13 195);
      background: color-mix(in srgb, oklch(0.69 0.13 195) 12%, transparent);
      border: 1px solid color-mix(in srgb, oklch(0.69 0.13 195) 30%, transparent);
      padding: 1px 6px;
      font-size: 10px; /* type-scale floor */
      font-weight: 600;
      letter-spacing: 0.04em;
      border-radius: 4px;
      text-transform: none;
    }

    .as__role-chip--you {
      color: var(--text);
      background: var(--pressed-overlay);
      border: 1px solid var(--divider);
      padding: 1px 6px;
      font-size: 10px; /* type-scale floor */
      font-weight: 600;
      letter-spacing: 0.04em;
      border-radius: 4px;
      text-transform: none;
    }

    @media (prefers-reduced-motion: reduce) {
      .as__search, .as__remove, .as__mode-btn { transition: none; }
    }
  `],
})
export class AttendeesSectionComponent implements OnDestroy {
  private readonly graphql = inject(GraphQLClientService);
  protected readonly meetingService = inject(BookMeetingService);
  private readonly identity = inject(ViewerIdentityService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  protected redactedAttendeesLabel(count: number): string {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.attendees.n_attendees', { count });
  }

  /** Driven by BookMeetingService.mode — when 'view', hides the search
   *  input, role/mode toggles, and the per-row remove button. Avatar chips
   *  and the host badge stay visible. */
  protected readonly readonly = computed(() => this.meetingService.mode() === 'view');

  /** True for the attendee that matches the logged-in viewer's email — drives
   *  the inline "You" badge in view-mode rows. Case-insensitive comparison
   *  because Graph and our backend can disagree on casing. */
  protected isViewer(att: MeetingAttendee): boolean {
    const viewerEmail = this.identity.viewer()?.email?.toLowerCase() ?? '';
    if (!viewerEmail) return false;
    return (att.email ?? '').toLowerCase() === viewerEmail;
  }

  /** Conflict status for an attendee in the currently-selected time
   *  window. Returns null when no conflict / unknown. Driven by the
   *  panel's `peopleAvailability` probe — see BookMeetingService.
   *  Case-insensitive email match (same rationale as `isViewer`). */
  protected conflictFor(att: MeetingAttendee): 'busy' | 'tentative' | 'oof' | null {
    const email = (att.email ?? '').toLowerCase();
    if (!email) return null;
    return this.meetingService.conflictsByAttendee().get(email) ?? null;
  }

  /** Inline availability for the meta row — drives the dot + label
   *  shown next to the attendee's department / email. Free when the
   *  conflict probe doesn't flag them; otherwise mapped to the
   *  conflict tone so the row reads as "• Busy" etc. */
  protected attendeeAvailability(att: MeetingAttendee): {
    key: 'free' | 'tentative' | 'busy' | 'oof';
    label: string;
    title: string;
  } {
    this.locale.locale();
    const t = (k: string) => this.transloco.translate(k);
    const conflict = this.conflictFor(att);
    if (!conflict) return { key: 'free', label: t('viewer.book_meeting_panel.attendees.avail_free'), title: t('viewer.book_meeting_panel.attendees.avail_free_title') };
    if (conflict === 'tentative')
      return { key: 'tentative', label: t('viewer.book_meeting_panel.attendees.avail_tentative'), title: t('viewer.book_meeting_panel.attendees.avail_tentative_title') };
    if (conflict === 'oof')
      return { key: 'oof', label: t('viewer.book_meeting_panel.attendees.avail_oof'), title: t('viewer.book_meeting_panel.attendees.avail_oof_title') };
    return { key: 'busy', label: t('viewer.book_meeting_panel.attendees.avail_busy'), title: t('viewer.book_meeting_panel.attendees.avail_busy_title') };
  }

  /** Map an attendee's response status to a tone + label for the view-mode
   *  status pill. Returns null when there's nothing meaningful to show
   *  (e.g., a fresh create-flow attendee with no RSVP state) so the row
   *  falls back to the on-site/remote mode badge. */
  protected responsePill(
    att: MeetingAttendee,
  ): { tone: 'ok' | 'warn' | 'danger' | 'neutral' | 'info'; label: string } | null {
    if (att.isOrganiser) {
      // Organisers don't RSVP — no pill, the "Organizer" name chip is enough.
      return null;
    }
    this.locale.locale();
    const t = (k: string) => this.transloco.translate(k);
    switch (att.responseStatus) {
      case 'accepted': return { tone: 'ok', label: t('viewer.book_meeting_panel.attendees.status.accepted') };
      case 'tentativelyAccepted': return { tone: 'warn', label: t('viewer.book_meeting_panel.attendees.status.tentative') };
      case 'declined': return { tone: 'danger', label: t('viewer.book_meeting_panel.attendees.status.declined') };
      case 'notResponded': return { tone: 'neutral', label: t('viewer.book_meeting_panel.attendees.status.pending') };
      default: return null;
    }
  }

  protected readonly searchQuery = signal('');
  private readonly results = signal<SearchResult[]>([]);
  protected readonly showResults = signal(false);
  /** Distinguishes a genuine empty match from a network/auth failure
   *  so the user sees a "Couldn't search · Try again" state instead
   *  of being silently misled. */
  protected readonly searchError = signal(false);
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;

  protected readonly isEmailQuery = computed(() =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.searchQuery().trim()),
  );

  protected readonly hasExternal = computed(() =>
    this.meetingService.attendees().some(a => a.external),
  );

  protected readonly filteredResults = computed(() => {
    const existing = new Set(
      this.meetingService.attendees().map(a => a.email.toLowerCase()),
    );
    return this.results().filter(r => !existing.has(r.email.toLowerCase()));
  });

  protected initials(name: string): string { return getInitials(name); }
  protected hue(name: string): number { return stringHue(name); }

  protected avatarBg(name: string): string {
    return `hsl(${stringHue(name)} 32% 28%)`;
  }

  protected avatarFg(name: string): string {
    return `hsl(${stringHue(name)} 70% 82%)`;
  }

  protected locationLabel(location: string): string {
    this.locale.locale();
    const t = (k: string) => this.transloco.translate(k);
    const map: Record<string, string> = {
      'in-office': t('viewer.book_meeting_panel.attendees.loc_in_office'),
      'remote': t('viewer.book_meeting_panel.attendees.loc_remote'),
      'on-leave': t('viewer.book_meeting_panel.attendees.loc_on_leave'),
      'external': t('viewer.book_meeting_panel.attendees.loc_external'),
    };
    return map[location] || location;
  }

  // ── Event handlers ────────────────────────────

  protected onInput(value: string): void {
    this.searchQuery.set(value);
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    if (value.trim().length < 2) {
      this.results.set([]);
      this.showResults.set(false);
      return;
    }
    this.searchTimeout = setTimeout(() => this.search(value.trim()), 300);
  }

  protected onFocus(): void {
    if (this.results().length > 0 || this.searchQuery().trim().length >= 2) {
      this.showResults.set(true);
    }
  }

  protected onBlur(): void {
    setTimeout(() => this.showResults.set(false), 200);
  }

  protected onEnter(): void {
    if (this.isEmailQuery()) this.addExternalByEmail();
  }

  protected addPerson(person: SearchResult): void {
    const attendee: MeetingAttendee = {
      id: person.id,
      name: person.name,
      email: person.email,
      isHost: false,
      isOrganiser: false,
      picture: person.picture ?? undefined,
      department: person.department ?? undefined,
      mode: 'in-person',
      // location is not hardcoded — left undefined until real presence data is available
    };
    this.meetingService.addAttendee(attendee);
    this.searchQuery.set('');
    this.results.set([]);
    this.showResults.set(false);
  }

  protected addExternalByEmail(): void {
    const email = this.searchQuery().trim();
    if (!email) return;
    const id = 'ext-' + email.replace(/[^a-z0-9]/gi, '');
    const nameFromEmail = email
      .split('@')[0]
      .replace(/[._-]/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
    const domain = email.split('@')[1];

    const attendee: MeetingAttendee = {
      id, name: nameFromEmail, email,
      isHost: false, isOrganiser: false,
      department: this.transloco.translate('viewer.book_meeting_panel.attendees.external_domain', { domain }),
      office: this.transloco.translate('viewer.book_meeting_panel.attendees.external'),
      mode: 'in-person',
      location: 'external',
      external: true,
    };
    this.meetingService.addAttendee(attendee);
    this.searchQuery.set('');
    this.showResults.set(false);
  }

  protected makeHost(id: string): void {
    this.meetingService.attendees.update(as =>
      as.map(a => ({
        ...a,
        isHost: a.id === id,
        isOrganiser: a.id === id ? false : a.isOrganiser,
      })),
    );
  }

  protected setOrganiser(id: string): void {
    this.meetingService.attendees.update(as =>
      as.map(a => a.id === id ? { ...a, isHost: false, isOrganiser: true } : a),
    );
  }

  protected makeAttendee(id: string): void {
    this.meetingService.attendees.update(as =>
      as.map(a => a.id === id ? { ...a, isHost: false, isOrganiser: false } : a),
    );
  }

  protected setMode(id: string, mode: AttendeeMode): void {
    this.meetingService.attendees.update(as =>
      as.map(a => a.id === id ? { ...a, mode } : a),
    );
  }

  // ── Schedule V2 — combined role + presence chip glue ──────────

  /** Derive the chip's role from the existing flags so the new chip
   *  reads the right state without a separate signal. */
  protected attRole(att: { isHost?: boolean; isOrganiser?: boolean; meetingRole?: AttendeeRole }): AttendeeRole {
    if (att.meetingRole) return att.meetingRole;
    if (att.isHost) return 'host';
    if (att.isOrganiser) return 'organiser';
    return 'attendee';
  }

  protected attPresence(att: { mode?: AttendeeMode; presence?: AttendeePresence }): AttendeePresence {
    if (att.presence) return att.presence;
    return att.mode === 'remote' ? 'remote' : 'onsite';
  }

  protected onChipRoleChange(id: string, role: AttendeeRole): void {
    const att = this.meetingService.attendees().find(a => a.id === id);
    if (!att?.email) return;
    this.meetingService.setAttendeeRole(att.email, role);
  }

  protected onChipPresenceChange(id: string, presence: AttendeePresence): void {
    const att = this.meetingService.attendees().find(a => a.id === id);
    if (!att?.email) return;
    this.meetingService.setAttendeePresence(att.email, presence);
  }

  // ── Search ────────────────────────────────────

  private async search(term: string): Promise<void> {
    this.searchError.set(false);
    try {
      const data = await this.graphql.query<any[]>(EMPLOYEES_QUERY, {
        search: term,
        take: 8,
      });
      // GraphQLClientService returns null when the query throws (auth,
      // network, schema). Treat null as ERROR so the user sees a
      // retry affordance, not a misleading "no results".
      if (data === null) {
        this.results.set([]);
        this.searchError.set(true);
        this.showResults.set(true);
        return;
      }
      interface AttendeeSearchDto {
        _id: string;
        name?: string;
        firstName?: string;
        lastName?: string;
        email: string;
        department?: string;
        picture?: string | null;
      }
      const mapped: SearchResult[] = (data as AttendeeSearchDto[]).map((e) => ({
        id: e._id,
        name: e.name || `${e.firstName || ''} ${e.lastName || ''}`.trim() || e.email,
        email: e.email,
        department: e.department || null,
        picture: e.picture || null,
      }));
      this.results.set(mapped);
      this.showResults.set(mapped.length > 0);
    } catch {
      this.results.set([]);
      this.searchError.set(true);
      this.showResults.set(true);
    }
  }

  /** Retry the last search after a transient failure. */
  protected retrySearch(): void {
    const term = this.searchQuery().trim();
    if (term.length >= 2) void this.search(term);
  }

  ngOnDestroy(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  }
}
